import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, AlertCircle, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VideoCallPage = () => {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userName = searchParams.get('name') || 'Participant';
  const userEmail = searchParams.get('email') || '';
  const role = searchParams.get('role') || 'client';
  
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [jitsiApi, setJitsiApi] = useState(null);
  
  const jitsiContainerRef = useRef(null);
  const isPractitioner = role === 'practitioner';

  useEffect(() => {
    let api = null;
    
    const initJitsi = async () => {
      try {
        setIsLoading(true);
        
        // Get JaaS token from backend
        console.log('Requesting JaaS token from:', `${API}/video/jaas-token`);
        const response = await fetch(`${API}/video/jaas-token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            room_name: roomId,
            user_name: userName,
            user_email: userEmail,
            is_moderator: isPractitioner,
            avatar_url: ''
          })
        });
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('JaaS token error:', response.status, errorText);
          throw new Error(`Erreur ${response.status}: ${errorText || 'Impossible de générer le token'}`);
        }
        
        const data = await response.json();
        console.log('JaaS token received, app_id:', data.app_id);
        
        // Load JaaS external API script
        if (!window.JitsiMeetExternalAPI) {
          await new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = 'https://8x8.vc/vpaas-magic-cookie-4775eb67b0d745eeab00cb8b8c87a75e/external_api.js';
            script.async = true;
            script.onload = resolve;
            script.onerror = reject;
            document.body.appendChild(script);
          });
        }
        
        // Wait a bit for script to initialize
        await new Promise(resolve => setTimeout(resolve, 500));
        
        if (!window.JitsiMeetExternalAPI) {
          throw new Error('Échec du chargement de Jitsi');
        }
        
        // Initialize Jitsi Meet
        const domain = '8x8.vc';
        const roomName = `${data.app_id}/${roomId}`;
        
        const options = {
          roomName: roomName,
          parentNode: jitsiContainerRef.current,
          jwt: data.token,
          width: '100%',
          height: '100%',
          configOverwrite: {
            startWithAudioMuted: false,
            startWithVideoMuted: false,
            disableDeepLinking: true,
            prejoinPageEnabled: false,
            prejoinConfig: {
              enabled: false
            },
            enableWelcomePage: false,
            enableClosePage: false,
            disableInviteFunctions: true,
            hideConferenceSubject: true,
            subject: 'Consultation Opora Vie',
            defaultLanguage: 'fr',
            disableThirdPartyRequests: true,
            requireDisplayName: false,
            analytics: {
              disabled: true
            },
            // Disable lobby and waiting
            lobby: {
              enabled: false
            },
            // Disable features not needed
            filmstrip: {
              disableResizable: true
            },
            notifications: [],
            disabledNotifications: ['notify.chatMessages']
          },
          interfaceConfigOverwrite: {
            SHOW_JITSI_WATERMARK: false,
            SHOW_WATERMARK_FOR_GUESTS: false,
            SHOW_BRAND_WATERMARK: false,
            BRAND_WATERMARK_LINK: '',
            SHOW_POWERED_BY: false,
            SHOW_PROMOTIONAL_CLOSE_PAGE: false,
            DISABLE_JOIN_LEAVE_NOTIFICATIONS: false,
            MOBILE_APP_PROMO: false,
            HIDE_INVITE_MORE_HEADER: true,
            DISABLE_TRANSCRIPTION_SUBTITLES: true,
            TOOLBAR_BUTTONS: [
              'microphone',
              'camera',
              'desktop',
              'fullscreen',
              'chat',
              'hangup',
              'tileview',
              'settings'
            ],
            SETTINGS_SECTIONS: ['devices', 'language'],
            DEFAULT_BACKGROUND: '#1a1a2e',
            DEFAULT_LOCAL_DISPLAY_NAME: userName,
            DEFAULT_REMOTE_DISPLAY_NAME: 'Participant',
            LANG_DETECTION: false
          },
          userInfo: {
            displayName: userName,
            email: userEmail
          }
        };
        
        api = new window.JitsiMeetExternalAPI(domain, options);
        setJitsiApi(api);
        
        // Hide loading after iframe loads (timeout fallback)
        setTimeout(() => {
          setIsLoading(false);
        }, 5000);
        
        // Event listeners
        api.addListener('videoConferenceJoined', () => {
          console.log('Joined video conference');
          setIsLoading(false);
          toast.success('Connecté à la consultation');
        });
        
        // Also hide loading when iframe is ready
        api.addListener('browserSupport', () => {
          console.log('Browser supported, hiding loader');
          setIsLoading(false);
        });
        
        api.addListener('videoConferenceLeft', () => {
          console.log('Left video conference');
          toast.info('Consultation terminée');
          navigate(-1);
        });
        
        api.addListener('participantJoined', (participant) => {
          console.log('Participant joined:', participant);
          toast.info(`${participant.displayName || 'Un participant'} a rejoint la consultation`);
        });
        
        api.addListener('participantLeft', (participant) => {
          console.log('Participant left:', participant);
        });
        
        api.addListener('readyToClose', () => {
          console.log('Ready to close');
          navigate(-1);
        });
        
        api.addListener('errorOccurred', (error) => {
          console.error('Jitsi error:', error);
          if (error.error?.name !== 'conference.authenticationRequired') {
            toast.error('Une erreur est survenue');
          }
        });
        
      } catch (err) {
        console.error('Error initializing Jitsi:', err);
        setError(err.message || 'Erreur lors de la connexion à la visioconférence');
        setIsLoading(false);
      }
    };
    
    initJitsi();
    
    // Cleanup
    return () => {
      if (api) {
        api.dispose();
      }
    };
  }, [roomId, userName, userEmail, isPractitioner, navigate]);

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 max-w-md text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Erreur de connexion</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <div className="flex gap-3 justify-center">
            <Button variant="outline" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
            <Button onClick={() => window.location.reload()}>
              Réessayer
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 flex flex-col" data-testid="video-call-page">
      {/* Loading overlay - semi-transparent to see what's behind */}
      {isLoading && (
        <div className="absolute inset-0 bg-gray-900/80 flex items-center justify-center z-50">
          <div className="text-center bg-gray-800/90 p-8 rounded-2xl">
            <Loader2 className="w-12 h-12 text-sage-500 animate-spin mx-auto mb-4" />
            <p className="text-white text-lg">Connexion à la consultation...</p>
            <p className="text-gray-400 text-sm mt-2">Veuillez patienter...</p>
            <Button 
              variant="outline" 
              className="mt-4 text-white border-gray-600"
              onClick={() => setIsLoading(false)}
            >
              Afficher quand même
            </Button>
          </div>
        </div>
      )}
      
      {/* Jitsi container - full viewport */}
      <div 
        ref={jitsiContainerRef} 
        className="w-full"
        style={{ 
          height: '100vh',
          minHeight: '100vh',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0
        }}
      />
    </div>
  );
};

export default VideoCallPage;
